import { motion } from 'framer-motion';
import { Brain, TrendingUp, Eye, Lightbulb, Smartphone } from 'lucide-react';

const insights = [
  {
    icon: TrendingUp,
    title: 'Growth Pattern',
    text: 'Your reliability score has improved 12% over the last 3 months.',
    type: 'positive',
  },
  {
    icon: Eye,
    title: 'Blind Spot',
    text: 'Communication delays tend to spike on Monday mornings.',
    type: 'insight',
  },
  {
    icon: Lightbulb,
    title: 'Strength',
    text: 'You consistently exceed expectations on creative deliverables.',
    type: 'strength',
  },
];

const insightBgClass = (type) => {
  if (type === 'positive') return 'bg-emerald-500/20';
  if (type === 'insight') return 'bg-amber-500/20';
  return 'bg-white/10';
};

const insightIconClass = (type) => {
  if (type === 'positive') return 'text-emerald-400';
  if (type === 'insight') return 'text-amber-400';
  return 'text-white';
};

const insightPositionClass = (index) => {
  if (index === 0) return 'top-0 left-0 lg:left-10';
  if (index === 1) return 'top-[160px] right-0 lg:right-0';
  return 'bottom-0 left-0 lg:left-20';
};

const IntrowspectSection = () => {
  return (
    <section
      id="features"
      className="relative overflow-hidden py-20 sm:py-28 px-6 md:px-12"
      data-testid="introwspect-section"
      style={{ background: 'linear-gradient(135deg, #0F1B2D 0%, #162640 40%, #1A2F4D 70%, #0F1B2D 100%)' }}
    >
      {/* Grid pattern overlay */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
        backgroundSize: '60px 60px',
      }} />

      {/* Glow accents */}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#C4A35A] rounded-full blur-[120px] opacity-[0.06]" />
      <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-cyan-500 rounded-full blur-[100px] opacity-[0.04]" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 border border-[#C4A35A]/40 bg-[#C4A35A]/10 flex items-center justify-center">
                <Brain size={20} className="text-[#C4A35A]" />
              </div>
              <div>
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#F5F3EE] font-bold block">
                  Introwspect<sup className="text-[8px]">™</sup>
                </span>
                <span className="text-[9px] uppercase tracking-[0.15em] text-[#9B9589] font-medium">
                  Behavioral Intelligence Algorithm
                </span>
              </div>
            </div>

            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#F5F3EE] tracking-tight mb-10">
              The algorithm that understands
              <span className="block text-[#C4A35A]/70 italic">how you work.</span>
            </h2>

            <p className="text-[#B0ACA4] font-medium leading-relaxed mb-8 max-w-lg">
              Introwspect is AINAA's proprietary behavioral intelligence system. It analyses your work patterns, professional interactions, and growth trajectories to surface insights you'd never find on your own — strengths, blind spots, and actionable improvements.
            </p>

            <div className="space-y-4 mb-8">
              {['Pattern recognition across your professional history', 'Blind spot detection in communication and reliability', 'Growth tracking with actionable recommendations'].map((feature, index) => (
                <motion.div
                  key={feature}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-center gap-3"
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-[#C4A35A]" />
                  <span className="text-sm font-semibold text-[#B0ACA4]">{feature}</span>
                </motion.div>
              ))}
            </div>

            <div className="border border-[#C4A35A]/30 bg-[#C4A35A]/5 p-3 inline-flex items-center gap-2">
              <Smartphone size={14} className="text-[#C4A35A]" />
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#C4A35A] font-bold">
                Available exclusively on AINAA Mobile App
              </p>
            </div>
          </motion.div>

          {/* Floating Insight Cards */}
          <div className="relative lg:h-[500px]">
            {/* Mobile: stacked cards */}
            <div className="flex flex-col gap-4 lg:hidden">
              {insights.map((insight, index) => (
                <motion.div
                  key={insight.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="border border-white/10 bg-white/5 backdrop-blur-md p-5">
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${insightBgClass(insight.type)}`}>
                        <insight.icon size={14} className={insightIconClass(insight.type)} />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-[#9B9589] mb-1">{insight.title}</p>
                        <p className="text-sm font-medium text-[#B0ACA4] leading-relaxed">{insight.text}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Desktop: floating positioned cards */}
            <div className="hidden lg:block relative h-[500px]">
              {insights.map((insight, index) => (
                <motion.div
                  key={insight.title}
                  initial={{ opacity: 0, y: 40, x: index % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, y: 0, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 + index * 0.2 }}
                  viewport={{ once: true }}
                  className={`absolute ${insightPositionClass(index)}`}
                  style={{
                    animation: `float 6s ease-in-out infinite`,
                    animationDelay: `${index * 2}s`,
                  }}
                >
                  <div className="border border-white/10 bg-white/5 backdrop-blur-md p-5 w-[280px]">
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${insightBgClass(insight.type)}`}>
                        <insight.icon size={14} className={insightIconClass(insight.type)} />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-[#9B9589] mb-1">{insight.title}</p>
                        <p className="text-sm font-medium text-[#B0ACA4] leading-relaxed">{insight.text}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}

              {/* Central glow */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-[#C4A35A] rounded-full blur-[80px] opacity-15" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IntrowspectSection;
